import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { PostFeedback } from '../../../api/feedBackApi';

// INTERFACE_FEEDBACK_REQUEST
interface FeedbackRequest {
  name: string;
  email: string;
  phone: string;
  feedback: string;
}

// INTERFACE_FEEDBACK_RESPONSE
interface FeedbackResponse {
  message: string;
  data?: any;
}

// INTERFACE_ERROR_DETAIL
interface ErrorDetail {
  code: string;
  detail: string;
  attr: string;
}

// INTERFACE_FEEDBACK_STATE
interface FeedbackState {
  data: FeedbackResponse | null;
  loading: boolean;
  error: string | null;
  validationErrors: ErrorDetail[] | null;
  success: boolean;
}

// INITIAL_STATE
const initialState: FeedbackState = {
  data: null,
  loading: false,
  error: null,
  validationErrors: null,
  success: false,
};

// ASYNC_THUNK - POST FEEDBACK
export const createFeedback = createAsyncThunk(
  'feedback/create',
  async (
    { requestData, language }: { requestData: FeedbackRequest; language?: 'az' | 'en' },
    { rejectWithValue }
  ) => {
    try {
      const result = await PostFeedback(requestData, language || 'az');

      if (result.status === 200 || result.status === 201) {
        return result.data as FeedbackResponse;
      } else {
        return rejectWithValue(result.data);
      }
    } catch (error) {
      return rejectWithValue({
        error: error instanceof Error ? error.message : 'Gözlənilməz xəta baş verdi'
      });
    }
  }
);

// FEEDBACK_SLICE
const feedbackSlice = createSlice({
  name: 'feedback',
  initialState,
  reducers: {
    // Reset feedback state
    resetFeedback: (state) => {
      state.data = null;
      state.loading = false;
      state.error = null;
      state.validationErrors = null;
      state.success = false;
    },
    // Clear error
    clearError: (state) => {
      state.error = null;
      state.validationErrors = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Feedback - Pending
      .addCase(createFeedback.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.validationErrors = null;
        state.success = false;
      })
      // Create Feedback - Fulfilled
      .addCase(createFeedback.fulfilled, (state, action: PayloadAction<FeedbackResponse>) => {
        state.loading = false;
        state.data = action.payload;
        state.success = true;
        state.error = null;
        state.validationErrors = null;
      })
      // Create Feedback - Rejected
      .addCase(createFeedback.rejected, (state, action) => {
        state.loading = false;
        state.success = false;

        const payload = action.payload as any;

        // Handle validation errors
        if (payload?.type === 'validation_error' && payload?.errors) {
          state.validationErrors = payload.errors;
          state.error = 'Məlumatların yoxlanılması zamanı xəta baş verdi';
        }
        // Handle generic errors
        else if (payload?.error) {
          state.error = payload.error;
        }
        // Fallback error
        else {
          state.error = 'Rəy göndərilməsi zamanı xəta baş verdi';
        }
      });
  },
});

// EXPORT_ACTIONS
export const { resetFeedback, clearError } = feedbackSlice.actions;

// EXPORT_REDUCER
export default feedbackSlice.reducer;